import { m } from "framer-motion";
import { useState, useCallback, useRef, useEffect } from "react";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { alpha } from "@mui/material/styles";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Stack, { StackProps } from "@mui/material/Stack";

import { paths } from "src/routes/paths";

import { useResponsive } from "src/hooks/use-responsive";

import Iconify from "src/components/iconify";
import { varFade, MotionViewport } from "src/components/animate";
import { useTranslation } from "react-i18next";
import { PAYMENT_METHODS } from "src/utils/applicationConstants";
import { t } from "i18next";
// ----------------------------------------------------------------------

const CARDS = [
  {
    title: "pricing_example_1_title",
    description: "pricing_example_1_description",
  },
  {
    title: "pricing_example_2_title",
    description: "pricing_example_2_description",
  },
  {
    title: "pricing_example_3_title",
    description: "pricing_example_3_description",
  },
];

const icons = PAYMENT_METHODS.map((method) => method.icon);

// const _homePlans = [...Array(3)].map((_, index) => ({
//   license: ["small_business", "medium_business", "large_business"][index],
//   size: ["1-50", "51-150", "151+"][index],
//   priceHUF: ["7 490", "15 990", "26 990"][index],
//   priceEUR: ["20", "40", "65"][index],
//   commission: "credit_card_payment_commission-text",

//   // commons: ["One end products", "12 months updates", "6 months of support"],
//   commons: [
//     "calendar_management",
//     "time_reservation",
//     "group_training_applications",
//     "online_payments",
//     "online_billing",
//     "statistics",
//     "cancellation_management",
//     "player_reservation",
//     "private_public_mode",
//   ],
//   options: [],
//   icons: icons,
// }));

const _homePlans = [...Array(2)].map((_, index) => ({
  license: ["usage_based_fee", "venue_based_fee", "large_business"][index],
  size: ["1-50", "51-150", "151+"][index],
  priceHUF: ["7 490", "15 990", "26 990"][index],
  priceEUR: ["20", "40", "65"][index],
  commission: [
    "credit_card_payment_commission_text",
    "credit_card_payment_commission_text_2",
  ][index],
  license_subtitle: ["usage_based_subtitle", "venue_based_subtitle"][index],
  license_comment: ["monthly_paying_client_description", ""][index],
  license_description: ["usage_based_description", "venue_based_description"][
    index
  ],
  // commons: ["One end products", "12 months updates", "6 months of support"],
  commons: [
    "calendar_management",
    "time_reservation",
    "group_training_applications",
    "online_payments",
    "online_billing",
    "statistics",
    "cancellation_management",
    "player_reservation",
    "private_public_mode",
  ],
  options: [],
  icons: icons,
}));

export default function HomePricing() {
  const { t } = useTranslation();
  const mdUp = useResponsive("up", "md");

  const [currentTab, setCurrentTab] = useState("Standard");

  const handleChangeTab = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      setCurrentTab(newValue);
    },
    []
  );

  const pricingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if the URL hash is '#pricing' after the component mounts
    if (window.location.hash === "#pricing" && pricingRef.current) {
      pricingRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const renderDescription = (
    <Stack spacing={3} sx={{ mb: 10, textAlign: "center" }}>
      <m.div variants={varFade().inUp}>
        <Typography
          component="div"
          variant="overline"
          sx={{ mb: 2, color: "text.disabled" }}
        >
          {t("our_prices")}
        </Typography>
      </m.div>

      <m.div variants={varFade().inDown}>
        <Typography variant="h2">
          {t("pricing")}
          <br />
          {t("for_our_partners")}
        </Typography>
      </m.div>

      <m.div variants={varFade().inDown}>
        <Typography sx={{ color: "text.secondary" }}>
          {t("grow_your_business")}
        </Typography>
      </m.div>
    </Stack>
  );

  const renderQuestions = (
    <Box
      sx={{
        textAlign: "center",
        mt: {
          xs: 5,
          md: 10,
        },
      }}
    >
      <Typography variant="h4">{t("still_have_questions")}</Typography>

      <Typography sx={{ mt: 2, mb: 5, color: "text.secondary" }}>
        {t("describe_your_case")}
      </Typography>

      <Button
        color="inherit"
        size="large"
        variant="contained"
        href="mailto:info@foggosport.com?subject=[Feedback] from Customer"
      >
        {t("contact_us")}
      </Button>
    </Box>
  );

  const renderReservationContent = (
    <>
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "repeat(1, 1fr)", // 1 column for extra-small screens (mobile)
          sm: "repeat(2, 1fr)", // 2 columns for small screens
          md: "repeat(2, 1fr)", // 3 columns for medium screens and above
        }}
        sx={{
          borderRadius: 2,
          border: (theme) => `dashed 1px ${theme.palette.divider}`,
        }}
      >
        {_homePlans.map((plan) => (
          <m.div key={plan.license} variants={varFade().in}>
            <PlanCard key={plan.license} plan={plan} />
          </m.div>
        ))}
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        py: { xs: 10, md: 15 },
        //bgcolor: (theme) => alpha(theme.palette.grey[500], 0.14),
        // bgcolor: (theme) => alpha(theme.palette.primary.dark, 0.08),
      }}
    >
      <Container component={MotionViewport}>
        {renderDescription}
        <Stack
          ref={pricingRef as React.Ref<HTMLDivElement>}
          id="pricing"
          alignContent={"center"}
          alignItems={"center"}
        >
          <Typography variant="h3" sx={{ mb: 2 }} color="primary.light">
            {t("reservation_module")}
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }} color="grey.600">
            {t("facility")}
            {" - "}
            {t("gym")}
            {" - "}
            {t("coach")}
            {" - "}
            {t("sportclub")}
          </Typography>
        </Stack>
        {renderReservationContent}

        {/* <Typography variant="h3" sx={{ my: 2 }} color="primary.light">
          {t("tournamentorganising_title")}
        </Typography> */}
        {renderQuestions}
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

// ----------------------------------------------------------------------

interface PlanCardProps extends StackProps {
  plan: {
    license: string;
    license_subtitle: string;
    license_comment: string;
    license_description: string;
    commons: string[];
    options: string[];
    icons: string[];
    size: string;
    priceEUR: string;
    priceHUF: string;
    commission: string;
  };
}

function PlanCard({ plan, sx, ...other }: PlanCardProps) {
  const {
    license,
    license_subtitle,
    license_comment,
    license_description,
    commons,
    options,
    icons,
    size,
    priceEUR,
    priceHUF,
    commission,
  } = plan;

  const plus = license === "Standard Plus";

  return (
    <Stack
      spacing={5}
      sx={{
        p: 5,
        pt: 7,
        ...(plus && {
          borderLeft: (theme) => `dashed 1px ${theme.palette.divider}`,
          borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          ...sx,
        }),
      }}
      {...other}
    >
      <Stack spacing={2}>
        <Typography
          variant="overline"
          component="div"
          sx={{ color: "text.disabled" }}
        >
          {t("monthly_subscription")}
        </Typography>

        <Box sx={{ position: "relative" }}>
          {/* <Typography variant="h4">{t(license)}</Typography> */}
          {/* <Typography variant="subtitle2">
            {size} {t("monthly_paying_client")} *
          </Typography> */}
          <Typography variant="h5" color="secondary.light">
            {t(license)}
          </Typography>
          <Typography variant="subtitle1" color="default">
            {t(license_subtitle)}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" spacing={2}>
        {icons.map((icon) => (
          <Iconify key={icon} icon={icon} width={20} color={"primary.main"} />
        ))}
      </Stack>

      <Stack spacing={2.5}>
        {commons.map((option) => (
          <Stack key={option} spacing={1} direction="row" alignItems="center">
            <Iconify icon="eva:checkmark-fill" width={16} />
            <Typography variant="body2">{t(option)}</Typography>
          </Stack>
        ))}

        {/* <Divider sx={{ borderStyle: "dashed" }} />

        {options.map((option, optionIndex) => {
          const disabled =
            (standardLicense && optionIndex === 1) ||
            (standardLicense && optionIndex === 2) ||
            (standardLicense && optionIndex === 3) ||
            (plus && optionIndex === 3);

          return (
            <Stack
              spacing={1}
              direction="row"
              alignItems="center"
              sx={{
                ...(disabled && { color: "text.disabled" }),
              }}
              key={option}
            >
              <Iconify
                icon={disabled ? "mingcute:close-line" : "eva:checkmark-fill"}
                width={16}
              />
              <Typography variant="body2">{t(option)}</Typography>
            </Stack>
          );
        })} */}

        <Stack spacing={1} direction="column" alignItems="left">
          <Typography variant="caption" color="default">
            {t(commission)}
          </Typography>
          <Typography variant="caption" color="default">
            {license_comment && "*" + t(license_comment)}
          </Typography>
          <Typography variant="caption" color="default">
            {t(license_description)}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
}

// const renderContent = (
//   <>
//     <m.div variants={varFade().in}>
//       <Box
//         sx={{
//           textAlign: "left",
//           mt: {
//             xs: 5,
//             md: 10,
//           },
//         }}
//       >
//         <m.div variants={varFade().inDown}>
//           <Typography variant="h4">{t("system_functions_fee")}</Typography>
//         </m.div>

//         <m.div variants={varFade().inDown}>
//           <Typography sx={{ mt: 2, mb: 3, color: "text.primary" }}>
//             1) {t("timebooking_functions_title")}
//             <b> {t("timebooking_functions_fee")}</b>
//           </Typography>
//           <Typography sx={{ mt: 2, mb: 3, color: "text.primary" }}>
//             2) {t("grouptraining_functions_title")}{" "}
//             <b> {t("grouptraining_functions_fee")}</b>
//           </Typography>

//           <Typography sx={{ mt: 2, mb: 5, color: "text.secondary" }}>
//             {t("venue_meaning_helper_description")}
//           </Typography>
//         </m.div>
//       </Box>

//       <Box
//         sx={{
//           textAlign: "left",
//           mt: {
//             xs: 5,
//             md: 10,
//           },
//         }}
//       >
//         <m.div variants={varFade().inDown}>
//           <Typography variant="h4">{t("payment_methods_fee")}</Typography>
//         </m.div>

//         <m.div variants={varFade().inDown}>
//           <Typography sx={{ mt: 2, mb: 0, color: "text.primary" }}>
//             1) {t("online_payment_automatic_billing_title")}
//             <b> {t("online_payment_automatic_billing_price")}</b>
//           </Typography>
//           <Typography sx={{ mt: 0, mb: 0, color: "text.secondary" }}>
//             {t("onsite_creditcard_banktransfer_payment_price")}
//           </Typography>
//           <Typography sx={{ mt: 0, mb: 3, color: "text.secondary" }}>
//             {t("creditcard_payment_commission")}
//           </Typography>

//           <Typography sx={{ mt: 2, mb: 0, color: "text.primary" }}>
//             2) {t("banktransfer_payment_automatic_billing_title")}
//             <b> {t("banktransfer_payment_automatic_billing_price")}</b>
//           </Typography>
//           <Typography sx={{ mt: 0, mb: 3, color: "text.secondary" }}>
//             {t("onsite_banktransfer_payment")}
//           </Typography>

//           <Typography sx={{ mt: 2, mb: 1, color: "text.primary" }}>
//             3) {t("only_onsite_payment_title")}
//             <b> {t("only_onsite_payment_price")}</b>
//           </Typography>
//         </m.div>
//       </Box>
//     </m.div>
//     <Typography variant="h4" sx={{ mt: 5, mb: 3, textAlign: "center" }}>
//       {t("pricing_examples_title")}
//     </Typography>
//     <m.div variants={varFade().in}>
//       <Box
//         gap={{ xs: 3, lg: 10 }}
//         display="grid"
//         alignItems="center"
//         gridTemplateColumns={{
//           xs: "repeat(1, 1fr)",
//           md: "repeat(3, 1fr)",
//         }}
//       >
//         {CARDS.map((card, index) => (
//           <m.div variants={varFade().inUp} key={card.title}>
//             <Card
//               sx={{
//                 textAlign: "center",
//                 boxShadow: { md: "none" },
//                 bgcolor: "#dbefff",
//                 p: (theme) => theme.spacing(5, 5),
//                 ...(index === 1 && {
//                   boxShadow: (theme) => ({
//                     md: `-40px 40px 80px ${
//                       theme.palette.mode === "light"
//                         ? alpha(theme.palette.grey[500], 0.16)
//                         : alpha(theme.palette.common.black, 0.4)
//                     }`,
//                   }),
//                 }),
//               }}
//             >
//               <Typography variant="h5" sx={{ mt: 1, mb: 1 }}>
//                 {t(card.title)}
//               </Typography>
//               <Typography sx={{ color: "text.secondary" }}>
//                 {t(card.description)}
//               </Typography>
//             </Card>
//           </m.div>
//         ))}
//       </Box>
//     </m.div>
// <m.div variants={varFade().in}>
//   <Box
//     sx={{
//       textAlign: "center",
//       mt: {
//         xs: 5,
//         md: 10,
//       },
//     }}
//   >
//     <m.div variants={varFade().inDown}>
//       <Typography variant="h4">{t("still_have_questions")}</Typography>
//     </m.div>

//     <m.div variants={varFade().inDown}>
//       <Typography sx={{ mt: 2, mb: 5, color: "text.secondary" }}>
//         {t("describe_your_case")}
//       </Typography>
//     </m.div>

//     <m.div variants={varFade().inUp}>
//       <Button
//         color="inherit"
//         size="large"
//         variant="contained"
//         href="mailto:info@foggo.hu?subject=[Feedback] from Customer"
//       >
//         {t("contact_us")}
//       </Button>
//     </m.div>
//   </Box>
// </m.div>
//   </>
// );
